/**
 * Internal behavioral analytics client for kurb.online.
 *
 * - Batches events and flushes every 5s or at 20 events
 * - Uses navigator.sendBeacon() on page unload to avoid lost events
 * - Respects cookieConsent cookie (same gate as GA4/Clarity)
 * - Manages anonymous_id (persistent) and session_id (30min timeout)
 *
 * SAFETY: Every public function is wrapped in try/catch so analytics
 * can never crash user-facing code. Failures are silently dropped.
 */

const API_URL = process.env.NEXT_PUBLIC_API_URL || "";

type EventCategory = "search" | "engagement" | "navigation" | "session" | "error";

interface AnalyticsEvent {
  event_name: string;
  event_category: EventCategory;
  session_id: string;
  anonymous_id: string;
  properties: Record<string, unknown>;
  page_path: string | undefined;
  device_type: "mobile" | "tablet" | "desktop" | undefined;
}

interface SessionInfo {
  session_id: string;
  anonymous_id: string;
  device_type?: "mobile" | "tablet" | "desktop";
  referrer?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
}

// --- Config ---
const FLUSH_INTERVAL_MS = 5000;
const FLUSH_THRESHOLD = 20;
const MAX_QUEUE_SIZE = 200;
const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const ANON_ID_KEY = "kurb_anon_id";
const SESSION_KEY = "kurb_session";
const SESSION_LAST_ACTIVITY_KEY = "kurb_session_ts";

// --- State ---
let eventQueue: AnalyticsEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let initialized = false;
let userId: string | null = null;

// --- Helpers ---

function isBrowser(): boolean {
  return typeof window !== "undefined";
}

function hasConsent(): boolean {
  if (!isBrowser()) return false;
  try {
    const match = document.cookie.match(/(?:^|;\s*)cookieConsent=([^;]*)/);
    return match?.[1] === "true";
  } catch {
    return false;
  }
}

function generateUUID(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getDeviceType(): "mobile" | "tablet" | "desktop" {
  if (!isBrowser()) return "desktop";
  const width = window.innerWidth;
  if (width < 768) return "mobile";
  if (width < 1024) return "tablet";
  return "desktop";
}

function safeGetItem(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    // localStorage/sessionStorage can throw in private browsing (Safari)
    return null;
  }
}

function safeSetItem(storage: Storage, key: string, value: string): void {
  try {
    storage.setItem(key, value);
  } catch {
    // Silently fail — private browsing or quota exceeded
  }
}

function safeRemoveItem(storage: Storage, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    // Silently fail — private browsing or storage access restrictions
  }
}

function getAnonymousId(): string {
  if (!isBrowser()) return "";
  let id = safeGetItem(localStorage, ANON_ID_KEY);
  if (!id) {
    id = generateUUID();
    safeSetItem(localStorage, ANON_ID_KEY, id);
  }
  return id;
}

function getSessionId(): string {
  if (!isBrowser()) return "";

  const now = Date.now();
  const lastActivity = Number(safeGetItem(sessionStorage, SESSION_LAST_ACTIVITY_KEY) || "0");
  let sessionId = safeGetItem(sessionStorage, SESSION_KEY);

  if (!sessionId || now - lastActivity > SESSION_TIMEOUT_MS) {
    sessionId = generateUUID();
    safeSetItem(sessionStorage, SESSION_KEY, sessionId);
  }

  safeSetItem(sessionStorage, SESSION_LAST_ACTIVITY_KEY, String(now));
  return sessionId;
}

/**
 * Replace any existing client identity with a fresh consented analytics identity.
 * This is useful when a pre-consent click created a minimal checkbot identity.
 */
export function regenerateAnalyticsIdentity(): void {
  if (!isBrowser()) return;

  try {
    safeSetItem(localStorage, ANON_ID_KEY, generateUUID());
    safeSetItem(sessionStorage, SESSION_KEY, generateUUID());
    safeSetItem(sessionStorage, SESSION_LAST_ACTIVITY_KEY, String(Date.now()));
  } catch {
    // Identity setup must never affect consent flow.
  }
}

function getUTMParams(): Record<string, string> {
  if (!isBrowser()) return {};
  try {
    const params = new URLSearchParams(window.location.search);
    const utm: Record<string, string> = {};
    for (const key of ["utm_source", "utm_medium", "utm_campaign"]) {
      const val = params.get(key);
      if (val) utm[key] = val;
    }
    return utm;
  } catch {
    return {};
  }
}

function buildSessionInfo(): SessionInfo {
  return {
    session_id: getSessionId(),
    anonymous_id: getAnonymousId(),
    device_type: getDeviceType(),
    referrer: isBrowser() ? document.referrer || undefined : undefined,
    ...getUTMParams(),
  };
}

// --- Flush ---

function flush(): void {
  if (!eventQueue.length) return;

  try {
    const events = eventQueue.splice(0);
    const payload = JSON.stringify({
      events,
      session: buildSessionInfo(),
      user_id: userId,
    });

    // Use sendBeacon if available (works during page unload), otherwise fetch
    let sent = false;
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      try {
        sent = navigator.sendBeacon(
          `${API_URL}/api/v2/events/batch`,
          new Blob([payload], { type: "application/json" })
        );
      } catch {
        // sendBeacon can throw if payload exceeds browser limit (~64KB)
        sent = false;
      }
    }

    if (!sent) {
      fetch(`${API_URL}/api/v2/events/batch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: payload,
        keepalive: true,
      }).catch(() => {
        // Silently drop failed analytics — never affect user experience
      });
    }
  } catch {
    // If anything goes wrong during flush, silently drop — never affect UX
    eventQueue = [];
  }
}

function startFlushTimer(): void {
  if (flushTimer) return;
  flushTimer = setInterval(flush, FLUSH_INTERVAL_MS);
}

function stopFlushTimer(): void {
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
}

// Keep a reference so we can remove on destroy
function handleVisibilityChange(): void {
  if (document.visibilityState === "hidden") {
    flush();
  }
}

// --- Public API ---

/**
 * Initialize the analytics client. Call once on app load.
 * Sets up periodic flushing and page unload handling.
 */
export function initAnalytics(clerkUserId?: string | null): void {
  if (!isBrowser() || initialized) return;

  try {
    userId = clerkUserId || null;
    initialized = true;

    startFlushTimer();

    window.addEventListener("beforeunload", flush);
    window.addEventListener("visibilitychange", handleVisibilityChange);
  } catch {
    // Initialization failed — analytics disabled for this session
  }
}

/**
 * Update the user ID (e.g. after sign-in).
 */
export function setAnalyticsUserId(id: string | null): void {
  userId = id;
}

/**
 * Track a behavioral event.
 * No-op if consent not given or not in browser.
 * Wrapped in try/catch — can never throw.
 */
export function trackEvent(
  eventName: string,
  category: EventCategory,
  properties: Record<string, unknown> = {}
): void {
  try {
    if (!isBrowser() || !hasConsent()) return;

    // Drop events if queue is too large (prevents unbounded memory growth)
    if (eventQueue.length >= MAX_QUEUE_SIZE) return;

    const event: AnalyticsEvent = {
      event_name: eventName,
      event_category: category,
      session_id: getSessionId(),
      anonymous_id: getAnonymousId(),
      properties,
      page_path: window.location.pathname,
      device_type: getDeviceType(),
    };

    eventQueue.push(event);

    if (eventQueue.length >= FLUSH_THRESHOLD) {
      flush();
    }
  } catch {
    // Silently drop — analytics must never crash user-facing code
  }
}

/**
 * Clean up (for testing or unmount).
 */
export function destroyAnalytics(): void {
  try {
    flush();
    stopFlushTimer();
    initialized = false;
    userId = null;

    if (isBrowser()) {
      window.removeEventListener("beforeunload", flush);
      window.removeEventListener("visibilitychange", handleVisibilityChange);
    }
  } catch {
    initialized = false;
    userId = null;
  }
}

/**
 * Stop analytics after consent is withdrawn without sending queued events.
 */
export function withdrawAnalyticsConsent(): void {
  eventQueue = [];
  stopFlushTimer();
  initialized = false;
  userId = null;

  if (!isBrowser()) return;

  window.removeEventListener("beforeunload", flush);
  window.removeEventListener("visibilitychange", handleVisibilityChange);
  safeRemoveItem(localStorage, ANON_ID_KEY);
  safeRemoveItem(sessionStorage, SESSION_KEY);
  safeRemoveItem(sessionStorage, SESSION_LAST_ACTIVITY_KEY);
}

// Re-export for convenience
export type { EventCategory };
